import { isLocal } from "services/utils";
import { ConnectionState, RobustWebSocket } from "./robustsocket";
import { getAccessToken, tryRefreshToken } from "services/backend";
import { StreamingState, StreamingStateMachine } from "./statemachine";
import { GanglionConversationResponse, isGanglionMessage } from "../../pages/chat/messagetypes";

export class GanglionConnection {
    constructor(opts?: {
        url?: string,
        onMessage: (msg: GanglionConversationResponse) => void,
        onStatus: (msg: GanglionConversationResponse) => void,
    }) {
        this._stateMachine = new StreamingStateMachine({
                onStatus: () => this._onStatusChange()
            });

        this.webSocket = new RobustWebSocket({
            url: opts?.url ?? (isLocal() ? "ws://localhost:20265/ws/interact" : "wss://ganglion.machineintelligence.com/ws/interact"),
            onMessage: async (data) => {
                try {
                    const msg = JSON.parse(data) as GanglionConversationResponse;
                    if (msg?.type == "status") {
                        const wasAuthenticated = this._authenticated;
                        this._authenticated = msg.authenticated;

                        if (!msg.authenticated) {
                            // todo: add an "_authInFlight" guard

                            if (await tryRefreshToken()) {
                                this._sendAuthentication();
                            }
                        } else if (!wasAuthenticated && msg.authenticated) {
                            // todo: resolve any promise waiting for authentication

                            // (re)attach to the conversation we're viewing so we receive its
                            // live stream - including messages sent from other clients / tabs.
                            this._sendCatchup();
                        }
                        return;
                    }
                    if (msg?.type == "catchup" || msg?.type == "user-message") {
                        // not part of this client's request/response cycle - hand straight to
                        // the page, don't run it through the streaming state machine.
                        opts?.onMessage(msg);
                        return;
                    }
                    this._stateMachine!.onMessageReceived(msg);
                    if (isGanglionMessage(msg)) {
                        if (msg.conversationId) {
                            this.conversationId = msg.conversationId;
                        }
                        //console.log("GANGLION MSG", msg)
                        opts?.onMessage(msg);
                    }
                }
                catch {

                }
            },
            onStateChange: (state, ev) => {
                console.log("NEW STATE " + state);
                switch (state) {
                    case ConnectionState.CONNECTED:
                        {
                            // Reset authentication state on new connection
                            this._authenticated = false;
                            this._sendAuthentication();
                        }
                        break;
                    case ConnectionState.DISCONNECTED:
                    case ConnectionState.FAILED:
                    case ConnectionState.BACKEND_UNRESPONSIVE:
                        {
                            // Clear state on disconnect
                            this._cleanupOnDisconnect();
                        }
                        break;
                }
            },
        });
    }

    conversationId: string | null = null;
    webSocket: RobustWebSocket | null = null;
    private _authenticated: boolean = false;
    private _model: string | null = null;
    private _stateMachine: StreamingStateMachine|null = null;
    private _sinceMessageId: number = 0;   // catchup cursor, resent on reconnect

    setModel(model: string) {
        this._model = model;
    }

    sendText(text: string): Promise<boolean> {
        const msg = JSON.stringify({
            conversationId: this.conversationId,
            message: text,
            type: "message",
            model: this._model ?? undefined
        });

        return new Promise((resolve) => {

            if (!this._authenticated || this.webSocket?.getState() !== ConnectionState.CONNECTED) {
                //     this._messageQueue.push({ data: msg, resolve });
                //     console.log("GanglionConnection: Message queued (not authenticated or not connected)");
                //     return;

                // todo: wait for the connectiont to become ready (with a timeout)

                resolve(false);
                return;
            }

            const sent = this._sendMessage(msg);
            resolve(sent);
        });
    }

    private _sendMessage(msg: string): boolean {
        const sent = this.webSocket?.send(msg);

        if (!sent) {
            console.error("GanglionConnection couldn't send the message");
            return false;
        }

        this._stateMachine!.onMessageSent();

        return true;
    }

    private _cleanupOnDisconnect(): void {
        // deliberately not clearing conversationId

        // Reset authentication state
        this._authenticated = false;

        // Clear response timeout
        this._stateMachine!.reset();
    }

    private _onStatusChange(): void {

    }

    sendStop() {
        const msg = JSON.stringify({
            conversationId: this.conversationId,
            type: "stop"
        });
        this.webSocket?.send(msg);
    }

    /**
     * Point this connection at a conversation and subscribe to its live stream. Safe to call
     * before the socket is authenticated - the subscription is (re)sent once auth completes.
     * `sinceMessageId` is the highest per-conversation message index the caller already has;
     * catchup returns only newer messages. Call again to advance the cursor after merging a batch.
     */
    viewConversation(conversationId: string | null, sinceMessageId: number = 0) {
        const changed = this.conversationId !== conversationId;
        this.conversationId = conversationId;
        this._sinceMessageId = sinceMessageId;
        if (conversationId && changed) this._sendCatchup();
    }

    private _sendCatchup() {
        if (!this._authenticated || !this.conversationId) return;
        if (this.webSocket?.getState() !== ConnectionState.CONNECTED) return;
        this.webSocket.send(JSON.stringify({
            type: "catchup",
            conversationId: this.conversationId,
            sinceMessageId: this._sinceMessageId
        }));
    }

    close() {
        this._cleanupOnDisconnect();
        this.webSocket?.close();
        this.webSocket = null;
    }

    getConnectionState(): ConnectionState | null {
        return this.webSocket?.getState() ?? null;
    }

    isAuthenticated(): boolean {
        return this._authenticated && this.webSocket?.isConnected() === true;
    }

    isReady(): boolean {
        return this.isAuthenticated() && this._stateMachine!.state == StreamingState.None;
    }

    // todo: promise to wait until we are in a readystate (and accept a timeout)

    private _sendAuthentication() {
        const token = getAccessToken();
        if (!token) {
            console.error("No access token available, skipping authentication");
            // todo: further handling, or escalate to the UI
            return;
        }
        this.webSocket?.send(JSON.stringify({ type: "authentication", token }));
    }
}
